import axios from '../utils/axios';
import { REFRESH_STORAGE_KEY } from '../constants';
import type {
  LoginInput,
  ProfileSaveInput,
  ResetPasswordInput,
  SessionUser,
  SignupInput,
} from '../types/index';
import {
  createLegacyResultError,
  createRequestError,
  getErrorStatus,
} from '../utils/requestErrors';

type LoginResult = {
  data: any;
  refreshToken: string;
};

type AuthenticationResult = {
  data: any;
  refreshToken: string;
};

function getResponseHeadersValue(headers: any, name: string): string {
  if (!headers) return '';
  if (typeof headers.get === 'function') {
    return headers.get(name) || '';
  }

  const normalizedName = String(name).toLowerCase();
  return String(headers[normalizedName] || headers[name] || '');
}

export async function login({ email, password }: LoginInput): Promise<LoginResult> {
  try {
    const response = await axios.post('/session/login', { email, password });
    return {
      data: response.data,
      refreshToken: getResponseHeadersValue(response.headers, 'x-refresh'),
    };
  } catch (error) {
    throw createRequestError(error);
  }
}

export async function createAccount(payload: SignupInput): Promise<any> {
  try {
    const response = await axios.post('/session/create', payload);
    return response.data;
  } catch (error) {
    const status = getErrorStatus(error);
    if (status === 400 || status === 502) {
      return createLegacyResultError(error);
    }
    throw createRequestError(error);
  }
}

export async function changeOrganization(organizationId: number | string): Promise<any> {
  try {
    const response = await axios.get('/session/changeorganization', {
      params: { organizationId },
    });
    return response.data;
  } catch (error) {
    throw createRequestError(error);
  }
}

export async function acceptEula(
  eulaVersion: string,
  organizationId?: number | string | null,
): Promise<AuthenticationResult> {
  try {
    const response = await axios.post('/session/accept-eula', null, {
      params: {
        eulaVersion,
        ...(organizationId ? { organizationId } : {}),
      },
    });
    return {
      data: response.data,
      refreshToken: getResponseHeadersValue(response.headers, 'x-refresh'),
    };
  } catch (error) {
    throw createRequestError(error);
  }
}

export async function refreshToken(organizationId: number | string): Promise<any> {
  const refreshTokenValue = window.localStorage.getItem(REFRESH_STORAGE_KEY);
  if (!refreshTokenValue) {
    throw new Error('No refresh token available');
  }

  try {
    const response = await axios.post('/session/refresh', null, {
      params: { organizationId },
      headers: {
        'x-refresh': refreshTokenValue,
      },
    });
    return response.data;
  } catch (error) {
    throw createRequestError(error);
  }
}

export async function forgotPassword(email: string): Promise<any> {
  try {
    const response = await axios.post('/session/resetpassword/request', {}, {
      params: { email },
    });
    return response.data;
  } catch (error) {
    const status = getErrorStatus(error);
    if (status === 400 || status === 502) {
      return createLegacyResultError(error);
    }
    throw createRequestError(error);
  }
}

export async function resetPassword(payload: ResetPasswordInput): Promise<any> {
  try {
    const response = await axios.post('/session/resetpassword', payload);
    return response.data;
  } catch (error) {
    const status = getErrorStatus(error);
    if (status === 400 || status === 502) {
      return createLegacyResultError(error);
    }
    throw createRequestError(error);
  }
}

export async function verifyEmail(email: string, token: string): Promise<any> {
  try {
    const response = await axios.get('/session/verify/email', {
      params: { email, token },
    });
    return response.data;
  } catch (error) {
    throw createRequestError(error);
  }
}

export async function changeName(name: string, userId = 0): Promise<any> {
  try {
    const response = await axios.post('/session/change/name', {
      userId,
      name,
    });
    return response.data;
  } catch (error) {
    throw createRequestError(error);
  }
}

export async function changeEmail(email: string, userId = 0): Promise<any> {
  try {
    const response = await axios.post('/session/change/email', {
      userId,
      email,
    });
    return response.data;
  } catch (error) {
    throw createRequestError(error);
  }
}

export async function changePassword(currentPassword: string, newPassword: string): Promise<any> {
  try {
    const response = await axios.post('/session/change/password', {
      currentPassword,
      password: newPassword,
    });
    return response.data;
  } catch (error) {
    const status = getErrorStatus(error);
    if (status === 400 || status === 502) {
      return createLegacyResultError(error);
    }
    throw createRequestError(error);
  }
}

export async function changeDefaultOrganization(organizationId: number | null): Promise<any> {
  try {
    const response = await axios.post('/session/change/defaultorganization', {
      organizationId,
    });
    return response.data;
  } catch (error) {
    throw createRequestError(error);
  }
}

export async function saveCurrentUserProfile(
  updatedUser: ProfileSaveInput,
  currentUser: SessionUser | null,
): Promise<{
  emailChanged: boolean;
  updatedUser: SessionUser;
}> {
  const name = String(updatedUser?.name || '').trim();
  const email = String(updatedUser?.email || '').trim();
  const userId = Number(updatedUser?.id || currentUser?.id || 0) || 0;
  const nameChanged = Boolean(name && name !== currentUser?.name);
  const emailChanged = Boolean(email && email !== currentUser?.email);
  const changingPassword = Boolean(updatedUser?.currentPassword && updatedUser?.newPassword);
  const defaultOrganizationId = updatedUser.defaultOrganizationId !== undefined
    ? updatedUser.defaultOrganizationId
    : currentUser?.defaultOrganizationId ?? null;
  const defaultOrganizationChanged = defaultOrganizationId !== (currentUser?.defaultOrganizationId ?? null);

  if (nameChanged) {
    await changeName(name, userId);
  }

  if (changingPassword) {
    const passwordResult = await changePassword(
      String(updatedUser.currentPassword),
      String(updatedUser.newPassword),
    );

    if (passwordResult?.error) {
      return Promise.reject(
        createRequestError({
          response: {
            status: passwordResult.status,
            data: passwordResult.respBody,
          },
        }),
      );
    }
  }

  if (emailChanged) {
    await changeEmail(email, userId);
  }

  if (defaultOrganizationChanged) {
    await changeDefaultOrganization(defaultOrganizationId);
  }

  return {
    emailChanged,
    updatedUser: {
      ...currentUser,
      ...updatedUser,
      name,
      email,
      defaultOrganizationId,
    } as SessionUser,
  };
}
